import React, { useState, useEffect } from 'react';
import { Menu, User } from 'lucide-react';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import BandeauInstitutionnel from './BandeauInstitutionnel';

interface DashboardLayoutProps {
  children: React.ReactNode;
  /**
   * Les écrans de conversation gèrent eux-mêmes leur défilement (fil de
   * messages au-dessus, zone de saisie collée en bas) : on leur rend toute la
   * hauteur sans marge intérieure.
   */
  pleinCadre?: boolean;
}

/**
 * Coque des écrans de travail.
 *
 * La page entière tient dans la hauteur de la fenêtre : seul le contenu
 * défile, de sorte que le bandeau institutionnel reste visible en bas de tous
 * les écrans, sans jamais recouvrir la zone de saisie.
 */
const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, pleinCadre = false }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    const handleResize = () => setSidebarOpen(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="h-screen overflow-hidden bg-eg-fond text-eg-encre">
      <Sidebar isOpen={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

      <div className="flex h-screen flex-col lg:ml-60">
        {/* Sur grand écran le menu porte l'identité et le compte ; la barre
            du haut ne subsiste que sur mobile, pour rouvrir le menu. */}
        <header className="shrink-0 border-b border-eg-bordure bg-eg-fond/95 backdrop-blur lg:hidden">
          <div className="flex h-12 items-center gap-3 px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="rounded-lg p-1.5 text-eg-gris hover:bg-eg-voile"
              aria-label="Ouvrir le menu"
            >
              <Menu className="h-5 w-5" />
            </button>
            <span className="text-[14px] font-semibold tracking-tight">OmnIA</span>
            <Link
              to="/workspace/profile"
              className="ml-auto flex h-7 w-7 items-center justify-center rounded-full
                bg-eg-voile text-eg-gris"
              aria-label="Ouvrir le profil"
            >
              <User className="h-4 w-4" />
            </Link>
          </div>
        </header>

        <main
          id="zone-contenu"
          className={`min-h-0 flex-1 ${pleinCadre ? 'flex flex-col overflow-hidden' : 'overflow-auto p-4 lg:px-8 lg:py-7'}`}
        >
          {children}
        </main>

        <BandeauInstitutionnel className="shrink-0" />
      </div>
    </div>
  );
};

export default DashboardLayout;
